import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Save, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { MenuTreeEditor, flatKeys, emptyMenus } from "@/pages/PlanMaster";

export default function OrganisationAuthorisation() {
  const [orgs, setOrgs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [oid, setOid] = useState("");
  const [planId, setPlanId] = useState("none");
  const [overrides, setOverrides] = useState({}); // {menuKey: bool}
  const [planMenus, setPlanMenus] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/organisation").then(r => setOrgs(r.data)).catch(e => toast.error(formatErr(e)));
    api.get("/plans").then(r => setPlans(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (!oid && orgs[0]) setOid(orgs[0].id); }, [orgs, oid]);

  const load = () => {
    if (!oid) return;
    api.get(`/org-authorisations/${oid}`).then(r => {
      setPlanId(r.data.plan_id || "none");
      setOverrides(r.data.overrides || {});
      setPlanMenus(r.data.plan_menus || {});
      setDirty(false);
    }).catch(e => toast.error(formatErr(e)));
  };
  useEffect(load, [oid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update plan menus on plan switch (without saving yet)
  const onPlanChange = (v) => {
    setPlanId(v);
    setDirty(true);
    if (v === "none") { setPlanMenus({}); return; }
    const plan = plans.find(p => p.id === v);
    setPlanMenus(plan?.menus || {});
    // Do NOT clear overrides — spec says manual toggles override plan defaults.
  };

  // Resolved value = override if key in overrides, else plan default, else false
  const resolved = useMemo(() => {
    const out = { ...emptyMenus() };
    flatKeys.forEach(k => { out[k] = k in overrides ? !!overrides[k] : !!planMenus[k]; });
    return out;
  }, [overrides, planMenus]);

  const onMenuChange = (nextResolved) => {
    // Convert to override map: any key that differs from the plan default becomes an override; matching keys drop out.
    const ov = { ...overrides };
    flatKeys.forEach(k => {
      const planVal = !!planMenus[k];
      const cur = !!nextResolved[k];
      if (cur === planVal) delete ov[k];
      else ov[k] = cur;
    });
    setOverrides(ov);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/org-authorisations/${oid}`, { plan_id: planId === "none" ? null : planId, overrides });
      toast.success("Organisation authorisation saved");
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };
  const cancel = () => { load(); toast.info("Changes discarded"); };

  const overrideCount = Object.keys(overrides).length;
  const enabledCount = Object.values(resolved).filter(Boolean).length;

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><ShieldCheck className="w-3 h-3"/> Org & Access</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Organisation Authorisation</h1>
          <div className="text-xs text-muted-foreground mt-1">Enable or disable menus per organisation via plans or manual toggles</div>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Organisation</Label>
            <Select value={oid} onValueChange={setOid}>
              <SelectTrigger data-testid="orgauth-org" className="mt-1 rounded-sm bg-secondary border-border w-64"><SelectValue placeholder="Pick organisation"/></SelectTrigger>
              <SelectContent>{orgs.map(o => <SelectItem key={o.id} data-testid={`orgauth-org-opt-${o.id}`} value={o.id}>{o.business_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Plan</Label>
            <Select value={planId} onValueChange={onPlanChange}>
              <SelectTrigger data-testid="orgauth-plan" className="mt-1 rounded-sm bg-secondary border-border w-56"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem data-testid="orgauth-plan-opt-none" value="none">— No plan (custom) —</SelectItem>
                {plans.map(p => <SelectItem key={p.id} data-testid={`orgauth-plan-opt-${p.id}`} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="orgauth-cancel" variant="outline" onClick={cancel} disabled={!dirty || saving} className="rounded-sm text-xs uppercase tracking-wider h-10"><X className="w-4 h-4 mr-1"/> Cancel</Button>
          <Button data-testid="orgauth-save" onClick={save} disabled={!dirty || saving || !oid} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs h-10"><Save className="w-4 h-4 mr-1"/> Save</Button>
        </div>
      </div>

      {!oid ? (
        <div className="text-muted-foreground p-8 text-center">Create an organisation first in Organisation Master to configure its authorisation.</div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-3 text-xs">
            <div className="border border-border bg-card rounded-sm p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Enabled Menus</div><div className="text-2xl font-display font-black text-primary" data-testid="orgauth-enabled">{enabledCount} / {flatKeys.length}</div></div>
            <div className="border border-border bg-card rounded-sm p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Active Plan</div><div className="text-lg font-display font-black">{planId === "none" ? "— No plan —" : (plans.find(p => p.id === planId)?.name || "-")}</div></div>
            <div className="border border-primary/40 bg-primary/5 rounded-sm p-3"><div className="text-[10px] uppercase tracking-wider text-primary">Manual Overrides</div><div className="text-2xl font-display font-black" data-testid="orgauth-overrides">{overrideCount}</div></div>
          </div>
          <MenuTreeEditor value={resolved} onChange={onMenuChange} testPrefix="orgauth"/>
          <div className="text-xs text-muted-foreground mt-3">Toggles above the plan defaults become overrides for this organisation only. Clearing all overrides reverts to the plan defaults.</div>
        </>
      )}
    </div>
  );
}
